import React, { useMemo, useState } from 'react';
import { Plus, Download, Search, Upload } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { AppButton } from '../../../general/AppButton/AppButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import PreliminaryNoticesTable from '../PreliminaryNoticesTable/PreliminaryNoticesTable';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../redux/store';
import { PreliminaryNoticeApiResponse } from '@dill/dill-shared';
import { buildSupplierPath, downloadPDFsAsZip } from '../../../../utils/helpers';
import { AppErrorPage } from '../../../general/AppErrorPage/AppErrorPage';
import moment from 'moment';
import AppCustomDropdown from '../../../general/AppCustomDropdown/AppCustomDropdown';
import BulkAddManualPrelimNoticesModal from '../BulkAddManualPrelimNoticesModal/BulkAddManualPrelimNoticesModal';
import { getSupplierPreliminaryNotices } from '../../../../redux/services/preliminaryNoticesService';

const PreliminaryNotices = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState('');
  const { preliminaryNotices, loadingErrorList, selectedPreliminaryNotices } = useAppSelector(
    (state) => state.preliminaryNotices
  );
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [openDropdown, setOpenDropDown] = useState('');
  const [isBulkAddManualPrelimNoticesModalOpen, setIsBulkAddManualPrelimNoticesModalOpen] =
    useState(false);

  const error = useMemo(() => {
    if (loadingErrorList.length > 0) {
      const errorMessage: any = loadingErrorList.filter((obj) =>
        obj.hasOwnProperty('getSupplierPreliminaryNotices')
      );
      if (errorMessage.length > 0) {
        return errorMessage[0]?.getSupplierPreliminaryNotices;
      }
    }
    return null;
  }, [loadingErrorList]);

  const searchedPreliminaryNotices: PreliminaryNoticeApiResponse[] = useMemo(() => {
    let filteredPreliminaryNotices: PreliminaryNoticeApiResponse[] = [];
    if (preliminaryNotices && searchText) {
      const searched = preliminaryNotices.filter((preliminaryNotice) => {
        if (
          (preliminaryNotice.createdAt &&
            moment(preliminaryNotice.createdAt)
              .format('MM/DD/YYYY')
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job &&
            preliminaryNotice.job.name
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job?.jobNumber &&
            preliminaryNotice.job.jobNumber
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job?.buyer?.name &&
            preliminaryNotice.job?.buyer?.name
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job?.owner &&
            preliminaryNotice.job?.owner
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job?.GC &&
            preliminaryNotice.job?.GC.toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())) ||
          (preliminaryNotice.job?.lender &&
            preliminaryNotice.job?.lender
              .toString()
              .toLowerCase()
              .includes(searchText.toLowerCase()))
        ) {
          return true;
        }
        return false;
      });
      filteredPreliminaryNotices = searched;
    } else {
      filteredPreliminaryNotices = preliminaryNotices;
    }

    return filteredPreliminaryNotices;
  }, [preliminaryNotices, searchText]);

  const handleDownloadAll = () => {
    const downloadZipFiles = [] as { url: string; name: string }[];
    selectedPreliminaryNotices.forEach((preliminaryNotice) => {
      const createdAtTimeStamp = preliminaryNotice?.createdAt
        ? new Date(preliminaryNotice.createdAt).getTime()
        : new Date().getTime();
      if (preliminaryNotice.pdf) {
        downloadZipFiles.push({
          url: preliminaryNotice?.pdf?.url ?? '',
          name: `${preliminaryNotice.job?.name ?? ''}-${
            preliminaryNotice.job?.owner ?? ''
          }-${createdAtTimeStamp}`,
        });
      }
    });
    downloadPDFsAsZip(downloadZipFiles, 'preliminaryNotices');
  };

  const filteredPreliminaryNotice: PreliminaryNoticeApiResponse[] = useMemo(() => {
    if (selectedFilters.length === 0) {
      return searchedPreliminaryNotices;
    }

    return searchedPreliminaryNotices.filter((preliminaryNotice) => {
      for (const filter of selectedFilters) {
        if (filter.name === 'amount') {
          const paymentAmount = preliminaryNotice.fieldsData?.find(
            (item: any) => item.id === 'paymentAmount'
          );
          if (
            (filter.value?.min &&
              filter.value?.max &&
              (Number(paymentAmount?.value) < Number(filter.value.min) ||
                Number(paymentAmount?.value) > Number(filter.value.max))) ||
            !paymentAmount
          ) {
            return false;
          }
        } else if (filter.name === 'job') {
          if (!filter?.value?.includes(preliminaryNotice?.job?.id)) {
            return false;
          }
        }
      }
      return true;
    });
  }, [searchedPreliminaryNotices, selectedFilters]);

  const handleFilterSelect = (selectFilter: any[]) => {
    setSelectedFilters(selectFilter);
  };

  return (
    <div className="h-full p-4 flex ">
      <BulkAddManualPrelimNoticesModal
        open={isBulkAddManualPrelimNoticesModalOpen}
        handleClose={() => {
          setIsBulkAddManualPrelimNoticesModalOpen(false);
          dispatch(getSupplierPreliminaryNotices({ supplierId: currentSupplier?.id || '' }));
        }}
      />
      <div className="flex flex-col px-5 py-4 w-full bg-white">
        <div className="flex justify-between flex-wrap z-20">
          <div className="flex mb-2">
            <AppInputField
              inputFieldStyles={{ width: '200px' }}
              id="search"
              value={searchText}
              placeholder={'Search by recipient or job'}
              icon={<Search size={15} />}
              onTextChange={(text) => {
                setSearchText(text);
              }}
            />
          </div>
          <div className="flex mb-2 items-center justify-center">
            <AppButton
              text="Request Notice"
              icon={<Plus color={COLORS.WHITE} />}
              buttonStyles={{ padding: '8px', height: '40px' }}
              onClick={() => {
                navigate(
                  buildSupplierPath(
                    currentSupplier?.id,
                    '/preliminaryNotices/overview/addPreliminaryNotice'
                  )
                );
              }}
            />
            {selectedPreliminaryNotices.length > 0 && (
              <AppButton
                text="Bulk Download"
                buttonStyles={{ marginLeft: '5px', padding: '8px', height: '40px' }}
                type="TERTIARY"
                onClick={handleDownloadAll}
                icon={<Download size={20} color={COLORS.TEXT_SECONDARY} />}
              />
            )}
            <AppCustomDropdown
              buttonStyles={{ width: '40px', height: '40px' }}
              inputFieldStyles={{ marginLeft: '5px' }}
              onSelect={(val) => {
                if (val === 'manualUploadPrelimNotice') {
                  navigate(
                    buildSupplierPath(
                      currentSupplier?.id,
                      '/preliminaryNotices/overview/manualAddPreliminaryNotice'
                    )
                  );
                } else if (val === 'bulkUploadPreviousNotices') {
                  setIsBulkAddManualPrelimNoticesModalOpen(true);
                }
              }}
              options={[
                {
                  value: 'manualUploadPrelimNotice',
                  label: 'Upload Previous Notice',
                  icon: <Upload size={16} />,
                },
                {
                  label: 'Bulk Upload Previous Notices',
                  value: 'bulkUploadPreviousNotices',
                  icon: <Upload size={16} />,
                },
              ]}
              open={openDropdown === 'open'}
              onOpen={() => setOpenDropDown('open')}
              onClose={() => setOpenDropDown('')}
              width="240px"
            />
          </div>
        </div>
        {error ? (
          <AppErrorPage
            title="Error loading  Preliminary Notices."
            contactMessage="Please contact customer support."
            errorMessage={error}
          />
        ) : (
          <div>
            <PreliminaryNoticesTable
              searchedPreliminaryNotices={filteredPreliminaryNotice}
              refresh={refresh}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PreliminaryNotices;
